'use client';

import { Button } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { removeItems } from '@shared/components/cart/actions';
import BDImage from '@shared/components/elements/bd-image';
import { Spinner } from '@shared/components/layout/spinner';
import { LineTotalWithDiscount } from '@shared/components/price-line';
import { CartItem } from '@shared/lib/shopify/types';
import { Cart } from '@shared/lib/shopify/types/shopify-native';
import { getCartLineLink } from '@shared/lib/utils/cart-utils';
import { rOptionName } from '@shared/lib/utils/formats';
import Link from 'next/link';
import { useActionState } from 'react';

export default function HeaderListItem({
  lineItem,
  operatingCart,
  children,
}: {
  lineItem: CartItem;
  operatingCart: Cart;
  children?: React.ReactNode;
}) {
  const url = getCartLineLink(lineItem);

  const [removeError, removeCartAction, pendingRemove] = useActionState(
    removeItems.bind(null, {
      cartId: operatingCart.id,
      lineIds: [lineItem.id],
    }),
    null
  );

  return (
    <section
      aria-labelledby="cart-item-charges-header"
      className="relative flex flex-row gap-x-3 border-t border-slate-200 pt-1 first:border-t-0 first:pt-0"
      key={lineItem.id}
    >
      {pendingRemove && <Spinner className="h-12 w-12" />}
      <div className="flex w-1/3 shrink-0 flex-col">
        <div className="aspect-square w-full rounded-md border border-slate-200">
          {(() => {
            const image = lineItem.merchandise.image;
            return (
              image && (
                <BDImage
                  alt={image.altText || lineItem.merchandise.title}
                  src={image}
                  className="h-full w-full rounded-md object-cover object-center"
                />
              )
            );
          })()}
        </div>
      </div>
      <div className="flex w-full flex-col">
        {(() => {
          const productTitle = (
            <h2 className="text-base font-normal">
              {lineItem.quantity > 1 ? `${lineItem.quantity} × ` : ''}
              {lineItem.merchandise.product.title}
            </h2>
          );
          return url ? <Link href={url}>{productTitle}</Link> : productTitle;
        })()}

        <h2 id="diamond-heading" className="sr-only">
          Charges breakdown.
        </h2>
        <div className="flex flex-col gap-y-1 text-xs md:text-sm">
          <LineTotalWithDiscount lineItem={lineItem} />
        </div>
        <div className="flex flex-col pt-1 text-slate-500">
          {lineItem.merchandise.selectedOptions.map((option) => (
            <p key={option.name} className="text-xxs text-slate-600 md:text-xs">
              {rOptionName(option)}: {option.value}
            </p>
          ))}
        </div>

        <form action={removeCartAction} className="mt-auto pt-1 font-normal">
          <Button
            className="ml-auto flex cursor-pointer flex-row items-center gap-x-1 border-b border-transparent text-xs hover:border-slate-800 md:text-sm"
            type="submit"
            disabled={pendingRemove}
          >
            <XMarkIcon aria-hidden="true" className="h-4 w-4" />
            <span>Remove</span>
          </Button>
        </form>
        {children}
      </div>
    </section>
  );
}
